import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isAfter } from 'date-fns';
import axios from 'cccisd-axios';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import notification from 'cccisd-notification';
import Table from 'cccisd-table';
import { Formik, Form, Field, CccisdInput, CccisdWysiwyg } from 'cccisd-formik';
import ClickButton from 'cccisd-click-button';
import { Link } from 'cccisd-react-router';
import { formatDate } from '../../helpers';
import { getReporterProgress } from 'js/selectors/assessments';
import _find from 'lodash/find';
import style from './style.css';

const Boilerplate = window.cccisd.boilerplate;

const BulkMessaging = props => {
    const { assessment } = props;
    const [selectedTableRows, setSelectedTableRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const studentsWithoutEmail = new Map();

    useEffect(() => {
        setSelectedTableRows(incompleteSessions.map(reporter => reporter.id));
        setLoading(false);
    }, [incompleteSessions]);

    const reporterProgress = useSelector(state => getReporterProgress(state.app.assessment));

    const getOpenedSessions = sessions => {
        return sessions.filter(session => {
            return session.opensAt && isAfter(new Date(), new Date(session.opensAt));
        });
    };

    const openedSessions = getOpenedSessions(reporterProgress);

    const getCurrentSessionDeploymentInfo = reporter => {
        let currentSessionDeploymentInfo = { pawnId: null, deploymentId: null };
        if (reporter.sessionDeploymentInfo) {
            /* eslint-disable eqeqeq  */
            currentSessionDeploymentInfo = _find(
                Object.values(reporter.sessionDeploymentInfo),
                o => o.pawnId == reporter.pawn.pawnId
            ); // non-strict comparison for comparing string to int
        }
        return currentSessionDeploymentInfo;
    };

    const getSurveyLink = reporter => {
        const currentSessionDeploymentInfo = getCurrentSessionDeploymentInfo(reporter);
        const link = Boilerplate.url(
            `/sessionPlayer/${currentSessionDeploymentInfo.deploymentId}/${assessment.assignmentPlanId}/${
                reporter.sessionId
            }/${reporter.settings.reporter}/${reporter.pawn.pawnId}/${
                reporter.pawn.pawnHash
            }?s=${reporter.settings.options.join(',')}`
        );
        return link;
    };

    const incompleteSessions = openedSessions.filter(reporter => {
        const currentSessionDeploymentInfo = getCurrentSessionDeploymentInfo(reporter);
        if (reporter.reporter === 'Student' && !reporter.email) {
            studentsWithoutEmail.set(reporter.studentId, { id: reporter.studentId, name: reporter.name });
        }
        return (
            reporter.pawn.pawnId &&
            !reporter.completed &&
            reporter.isOpen === true &&
            !currentSessionDeploymentInfo.isCancelled &&
            reporter.email
        );
    });

    const onSubmit = async values => {
        const selectedReporters = incompleteSessions.filter(row => selectedTableRows.includes(row.id));
        try {
            const response = await axios.post(Boilerplate.route('api.assessment.bulkSendMessages'), {
                action: 'invite',
                sendMethod: 'email',
                data: selectedReporters.map(row => {
                    const currentSessionDeploymentInfo = getCurrentSessionDeploymentInfo(row);
                    return {
                        id: row.pawn.pawnId,
                        hash: row.pawn.pawnHash,
                        recipient: row.email,
                        actionUrl: getSurveyLink(row),
                        messageLogData: {
                            pawn_id: row.pawn.pawnId,
                            deployment_id: currentSessionDeploymentInfo.deploymentId,
                            action: 'assessment_plan_remind',
                        },
                    };
                }),
                messageInfo: {
                    subject: values.subject,
                    body: values.body,
                },
            });
            if (response && response.status === 200) {
                notification({ message: 'Emails have been sent.', type: 'success' });
                props.closeModal();
            }
        } catch (e) {
            notification({ message: 'An error occured sending email!', type: 'danger' });
            console.error(e);
        }
    };

    const tableData = incompleteSessions.map(reporter => ({
        id: reporter.id,
        studentId: reporter.studentId,
        reporter: reporter.reporter,
        name: reporter.name,
        email: reporter.email,
        lastSent: reporter.reminded,
        link: getSurveyLink(reporter),
    }));

    const columns = [
        { name: 'studentId', label: 'Student ID', sort: true, filter: true },
        { name: 'reporter', label: 'Reporter' },
        { name: 'name', label: 'Name', sort: true, filter: true },
        {
            name: 'email',
            label: 'Email',
            sort: true,
            filter: true,
            render: value => {
                if (!value) {
                    return (
                        <div style={{ textAlign: 'center' }}>
                            Add student email in <Link to="/manage">Manage Students</Link>
                        </div>
                    );
                }
                return <div style={{ textAlign: 'center' }}>{value}</div>;
            },
        },
        {
            name: 'lastSent',
            label: 'Last Sent Message',
            sort: true,
            filter: true,
            render: value => {
                return (
                    <div style={{ textAlign: 'center' }}>
                        {value && value[0] ? formatDate(new Date(value[0].sentAt)) : ''}
                    </div>
                );
            },
        }, // Should be last sent reminder OR invite? Currently just filtering by "assessment_plan_remind" nexus_messages_log action
        // { name: 'link', label: 'Link' },
    ];

    const validate = values => {
        const errors = {};
        if (!values.subject) {
            errors.subject = 'Email subject is required';
        }
        if (!values.body) {
            errors.body = 'Email body is required';
        }
        return errors;
    };

    if (loading) {
        return <Loader loading />;
    }

    return (
        <div className={style.bulkMessaging}>
            <div className={style.messagingHeader}>
                This tool sends a seperate email to each selected reporter. <br />{' '}
                {studentsWithoutEmail.size > 0 && (
                    <div>
                        <p>
                            Some students are missing email addresses. Visit <Link to="/manage">Manage Students</Link>{' '}
                            to set their email addresses.
                        </p>
                        <Modal
                            trigger={
                                <a className="btn btn-primary" href="#">
                                    View Students
                                </a>
                            }
                            title="Students Missing Email Addresses"
                            size="large"
                        >
                            <Table
                                columns={[
                                    { name: 'id', label: 'Student ID', sort: true, filter: true },
                                    { name: 'name', label: 'Name', sort: true, filter: true },
                                ]}
                                data={Array.from(studentsWithoutEmail.values())}
                            />
                        </Modal>
                    </div>
                )}
            </div>
            <div className={style.bulkMessagingContent}>
                <Table
                    columns={columns}
                    data={tableData}
                    isSimpleCheckboxes
                    selectedRows={selectedTableRows}
                    perPage={10}
                    rowSelectedCallback={selectedRows => {
                        if (selectedRows.length !== selectedTableRows.length) {
                            setSelectedTableRows(selectedRows);
                        }
                    }}
                />

                <div className={style.formRender}>
                    <Formik
                        initialValues={{
                            subject: `${assessment.label} Assessment Plan`,
                            body: `Assessment ${assessment.label} is available to take! <br/><br/> Click the button below to start <br/><br/>`,
                        }}
                        validate={validate}
                        onSubmit={onSubmit}
                        render={({ submitForm }) => (
                            <Form>
                                <Field name="subject" component={CccisdInput} label="Email Subject" required />
                                <div className={style.body}>
                                    <Field
                                        name="body"
                                        component={CccisdWysiwyg}
                                        label="Email Body"
                                        type="textarea"
                                        required
                                    />
                                </div>
                                Note: The assessment link and salutation will appear below the email body when it is
                                sent.
                                <br />
                                <ClickButton
                                    className="btn btn-primary"
                                    isLoading={loading}
                                    isConfirm
                                    isDisabled={selectedTableRows.length === 0}
                                    confirmationMessage={`Send an email to the ${selectedTableRows.length} selected reporters?`}
                                    onClick={() => submitForm()}
                                    title={`(${selectedTableRows.length}) Send`}
                                />
                            </Form>
                        )}
                    />
                </div>
            </div>
        </div>
    );
};

export default BulkMessaging;
